import {
  MANUSCRIPT_CONTENT_VERSION,
  normalizeManuscriptContent,
  plainTextFromManuscriptContent,
} from "./childrensBookManuscriptContent";

import {
  buildChildrensBookStructuredManuscriptDivision,
  suggestChildrensBookStructuredManuscriptDivision,
  suggestChildrensBookStructuredManuscriptDivisionByWordLimits,
} from "./childrensBookManuscriptContentDivision";

import {
  manuscriptFlowSlotWordLimit,
} from "./childrensBookManuscriptCapacity";

import {
  MANUSCRIPT_PAGE_MAXIMUM_WORDS,
} from "./childrensBookManuscriptUnitDivision";

import {
  unitRequiresText,
} from "./childrensBookUnitDiagnostics";


function clean(value) {
  return String(
    value ?? ""
  ).trim();
}


function normalizedValue(value) {
  return clean(
    value
  ).toLowerCase();
}


function safeArray(value) {
  return Array.isArray(value)
    ? value
    : [];
}


function hasOwn(
  value,
  key
) {
  return Boolean(
    value &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    Object.prototype
      .hasOwnProperty.call(
        value,
        key
      )
  );
}


function failure(
  code,
  error
) {
  return {
    ok: false,
    code,
    error,
  };
}


function comparableText(value) {
  return clean(
    value
  ).replace(
    /\s+/g,
    " "
  );
}


function structuredEntryContent(
  entry = {}
) {
  const normalized =
    normalizeManuscriptContent(
      entry.manuscriptContent
    );

  if (
    !normalized.ok ||
    !normalized.content
  ) {
    return failure(
      "manuscript_content_invalid",
      "A manuscript page contains invalid structured content."
    );
  }

  return {
    ok: true,
    content:
      normalized.content,
  };
}


function manuscriptFlowContinuationIdentity(
  block = {}
) {
  const blockId =
    clean(
      block?.id
    );

  const match =
    blockId.match(
      /^(.*)-part-(\d+)$/
    );

  if (
    match &&
    clean(
      match[1]
    ) &&
    Number(
      match[2]
    ) >= 2
  ) {
    return {
      rootId:
        clean(
          match[1]
        ),
      fragmentNumber:
        Number(
          match[2]
        ),
    };
  }

  return {
    rootId:
      blockId,
    fragmentNumber: 1,
  };
}


function manuscriptFlowBlockLayoutSignature(
  block = {}
) {
  const {
    id,
    runs,
    ...layout
  } = block || {};

  return JSON.stringify(
    layout
  );
}


function manuscriptFlowContinuationCanRejoin(
  previousBlock,
  previousIdentity,
  nextBlock,
  nextIdentity
) {
  if (
    !previousBlock ||
    !nextBlock ||
    !previousIdentity ||
    !nextIdentity
  ) {
    return false;
  }

  if (
    previousBlock.type !==
      nextBlock.type ||
    (
      nextBlock.type !==
        "paragraph" &&
      nextBlock.type !==
        "blockquote"
    )
  ) {
    return false;
  }

  if (
    previousIdentity.rootId !==
      nextIdentity.rootId ||
    nextIdentity.fragmentNumber !==
      previousIdentity.fragmentNumber +
        1
  ) {
    return false;
  }

  return (
    manuscriptFlowBlockLayoutSignature(
      previousBlock
    ) ===
    manuscriptFlowBlockLayoutSignature(
      nextBlock
    )
  );
}


function rejoinManuscriptFlowContinuation(
  previousBlock,
  nextBlock
) {
  const previousRuns =
    safeArray(
      previousBlock?.runs
    ).map(
      (run) => ({
        ...run,
      })
    );

  const nextRuns =
    safeArray(
      nextBlock?.runs
    ).map(
      (run) => ({
        ...run,
      })
    );

  const previousLast =
    previousRuns[
      previousRuns.length - 1
    ];

  const nextFirst =
    nextRuns[0];

  if (
    previousLast &&
    nextFirst &&
    typeof previousLast.text ===
      "string" &&
    typeof nextFirst.text ===
      "string" &&
    previousLast.text &&
    nextFirst.text &&
    !/\s$/.test(
      previousLast.text
    ) &&
    !/^\s/.test(
      nextFirst.text
    )
  ) {
    previousLast.text =
      `${previousLast.text} `;
  }

  return {
    ...previousBlock,
    runs: [
      ...previousRuns,
      ...nextRuns,
    ],
  };
}


function combinedStructuredContent(
  entries
) {
  const blocks = [];

  for (
    const entry of entries
  ) {
    const normalized =
      structuredEntryContent(
        entry
      );

    if (!normalized.ok) {
      return normalized;
    }

    for (
      const block of normalized
        .content
        .blocks
    ) {
      const nextIdentity =
        manuscriptFlowContinuationIdentity(
          block
        );

      const previousBlock =
        blocks[
          blocks.length - 1
        ];

      const previousIdentity =
        blocks.length
          ? manuscriptFlowContinuationIdentity(
              previousBlock
            )
          : null;

      const previousFragmentNumber =
        blocks.length
          ? Number(
              previousBlock
                ?._flowContinuationFragmentNumber ||
                previousIdentity
                  ?.fragmentNumber ||
                1
            )
          : 0;

      const effectivePreviousIdentity =
        previousIdentity
          ? {
              ...previousIdentity,
              fragmentNumber:
                previousFragmentNumber,
            }
          : null;

      if (
        manuscriptFlowContinuationCanRejoin(
          previousBlock,
          effectivePreviousIdentity,
          block,
          nextIdentity
        )
      ) {
        const rejoined =
          rejoinManuscriptFlowContinuation(
            previousBlock,
            block
          );

        Object.defineProperty(
          rejoined,
          "_flowContinuationFragmentNumber",
          {
            value:
              nextIdentity
                .fragmentNumber,
            enumerable: false,
            configurable: true,
          }
        );

        blocks[
          blocks.length - 1
        ] =
          rejoined;

        continue;
      }

      const nextBlock = {
        ...block,
        runs:
          safeArray(
            block?.runs
          ).map(
            (run) => ({
              ...run,
            })
          ),
      };

      Object.defineProperty(
        nextBlock,
        "_flowContinuationFragmentNumber",
        {
          value:
            nextIdentity
              .fragmentNumber,
          enumerable: false,
          configurable: true,
        }
      );

      blocks.push(
        nextBlock
      );
    }
  }

  return {
    ok: true,

    content: {
      version:
        MANUSCRIPT_CONTENT_VERSION,

      blocks,
    },
  };
}


/*
 * First live-flow slice:
 *
 * FLOW SLOT
 *   Existing Single Page that permits manuscript text.
 *
 * SKIP
 *   A physical page that intentionally accepts no flowing text,
 *   but does not end the surrounding manuscript stream.
 *
 * BARRIER
 *   A structure whose live-flow rules are not implemented yet.
 *   Text must not be redistributed across that boundary.
 *
 * Image-aware capacity comes later. Small and half-page images
 * remain text-bearing slots for this word-based projection, while
 * full-page imagery has zero flowing-text capacity.
 */
function classifyFlowUnit(
  unit = {}
) {
  const unitId =
    clean(
      unit?.id
    );

  const unitType =
    normalizedValue(
      unit?.unitType
    );

  const illustrationPlan =
    normalizedValue(
      unit?.illustrationPlan
    );

  if (
    unitType !==
      "single_page"
  ) {
    return {
      kind: "barrier",
      unitId,
      reason:
        "unsupported_unit_type",
    };
  }

  if (
    !unitRequiresText(
      unit
    )
  ) {
    return {
      kind: "skip",
      unitId,
      reason:
        "no_text",
    };
  }

  if (
    illustrationPlan ===
      "full_page" ||
    illustrationPlan ===
      "full_spread"
  ) {
    return {
      kind: "skip",
      unitId,
      reason:
        "full_page_illustration",
    };
  }

  if (
    illustrationPlan ===
      "integrated_text"
  ) {
    return {
      kind: "barrier",
      unitId,
      reason:
        "integrated_text_layout",
    };
  }

  return {
    kind: "slot",
    unitId,
    reason:
      "text_flow_page",
  };
}


function buildFlowRegions(
  units
) {
  const regions = [];
  const skippedUnits = [];
  const barrierUnits = [];

  let currentSlots = [];

  function flushRegion() {
    if (
      currentSlots.length
    ) {
      regions.push(
        currentSlots
      );

      currentSlots = [];
    }
  }

  for (
    const unit of safeArray(
      units
    )
  ) {
    const classification =
      classifyFlowUnit(
        unit
      );

    if (
      classification.kind ===
        "slot"
    ) {
      currentSlots.push(
        unit
      );

      continue;
    }

    if (
      classification.kind ===
        "skip"
    ) {
      skippedUnits.push({
        unitId:
          classification.unitId,
        reason:
          classification.reason,
      });

      continue;
    }

    flushRegion();

    barrierUnits.push({
      unitId:
        classification.unitId,
      reason:
        classification.reason,
    });
  }

  flushRegion();

  return {
    regions,
    skippedUnits,
    barrierUnits,
  };
}


function projectRegion({
  slotUnits,
  entriesByUnitId,
  wordLimit,
  slotWordLimitsByUnitId =
    null,
}) {
  const sourceEntries = [];

  for (
    const unit of slotUnits
  ) {
    const unitId =
      clean(
        unit?.id
      );

    const entry =
      entriesByUnitId.get(
        unitId
      );

    if (!entry) {
      continue;
    }

    if (
      !hasOwn(
        entry,
        "manuscriptContent"
      )
    ) {
      if (
        clean(
          entry?.manuscriptText
        )
      ) {
        return failure(
          "structured_content_required",
          "Automatic manuscript flow currently requires structured manuscript content."
        );
      }

      continue;
    }

    sourceEntries.push(
      entry
    );
  }

  const availablePageCount =
    slotUnits.length;

  if (
    sourceEntries.length ===
      0
  ) {
    return {
      ok: true,

      projectedPages: [],

      vacatedUnitIds:
        slotUnits.map(
          (unit) =>
            clean(
              unit?.id
            )
        ),

      requiredPageCount: 0,

      availablePageCount,

      additionalPagesRequired: 0,

      completeProjection: true,

      sourceText: "",

      projectedText: "",

      manuscriptTextPreserved:
        true,
    };
  }

  const combined =
    combinedStructuredContent(
      sourceEntries
    );

  if (!combined.ok) {
    return combined;
  }

  const sourceText =
    plainTextFromManuscriptContent(
      combined.content
    );

  if (
    !clean(
      sourceText
    )
  ) {
    return {
      ok: true,

      projectedPages: [],

      vacatedUnitIds:
        slotUnits.map(
          (unit) =>
            clean(
              unit?.id
            )
        ),

      requiredPageCount: 0,

      availablePageCount,

      additionalPagesRequired: 0,

      completeProjection: true,

      sourceText: "",

      projectedText: "",

      manuscriptTextPreserved:
        true,
    };
  }

  const normalizedBaseWordLimit =
    Number(
      wordLimit
    );

  const slotWordLimits =
    slotUnits.map(
      (unit) => {
        const unitId =
          clean(
            unit?.id
          );

        const hasExplicitLimit =
          Boolean(
            unitId &&
            slotWordLimitsByUnitId &&
            typeof slotWordLimitsByUnitId ===
              "object" &&
            Object.prototype.hasOwnProperty.call(
              slotWordLimitsByUnitId,
              unitId
            )
          );

        return hasExplicitLimit
          ? Number(
              slotWordLimitsByUnitId[
                unitId
              ]
            )
          : manuscriptFlowSlotWordLimit(
              unit,
              normalizedBaseWordLimit
            );
      }
    );

  if (
    slotWordLimits.some(
      (limit) =>
        !Number.isInteger(
          limit
        ) ||
        limit < 1
    )
  ) {
    return failure(
      "invalid_flow_slot_word_limit",
      "Automatic manuscript flow could not determine a safe word capacity for an existing text page."
    );
  }

    /*
     * Automatic writer editing follows one continuous structured
     * manuscript stream through the existing physical pages.
     *
     * Fill the current physical page first, then the following page,
     * and continue in order until the manuscript stream is exhausted
     * or an existing structural barrier is reached.
     *
     * The ordinary divider remains useful for estimating enough
     * hypothetical continuation capacities. Actual automatic
     * distribution is performed by the sequential per-page divider
     * for both ordinary pages and image-aware text pages.
     */
    const ordinaryDivision =
      suggestChildrensBookStructuredManuscriptDivision({
        manuscriptContent:
          combined.content,

        wordLimit:
          normalizedBaseWordLimit,
      });

    if (
      !ordinaryDivision.ok
    ) {
      return ordinaryDivision;
    }

    const hypotheticalOrdinaryWordLimits =
      Array.from(
        {
          length:
            ordinaryDivision
              .segments
              .length,
        },
        () =>
          normalizedBaseWordLimit
      );

    const division =
      suggestChildrensBookStructuredManuscriptDivisionByWordLimits({
        manuscriptContent:
          combined.content,

        wordLimits: [
          ...slotWordLimits,
          ...hypotheticalOrdinaryWordLimits,
        ],
      });

  if (
    !division.ok
  ) {
    return division;
  }

  const requiredPageCount =
    division
      .segments
      .length;

  const completeProjection =
    requiredPageCount <=
      availablePageCount;

  const occupiedSegments =
    division
      .segments
      .slice(
        0,
        availablePageCount
      );

  const projectedPages =
    occupiedSegments.map(
      (
        segment,
        index
      ) => ({
        unitId:
          clean(
            slotUnits[
              index
            ]?.id
          ),

        manuscriptText:
          segment
            .manuscriptText,

        manuscriptContent:
          segment
            .manuscriptContent,

        wordCount:
          segment
            .wordCount,
      })
    );

  const vacatedUnitIds =
    completeProjection
      ? slotUnits
          .slice(
            occupiedSegments
              .length
          )
          .map(
            (unit) =>
              clean(
                unit?.id
              )
          )
      : [];

  const projectedText =
    projectedPages
      .map(
        (page) =>
          page
            .manuscriptText
      )
      .join(
        "\n\n"
      );

  return {
    ok: true,

    projectedPages,

    vacatedUnitIds,

    requiredPageCount,

    availablePageCount,

    additionalPagesRequired:
      Math.max(
        0,
        requiredPageCount -
          availablePageCount
      ),

    completeProjection,

    sourceText,

    projectedText,

    manuscriptTextPreserved:
      completeProjection &&
      comparableText(
        projectedText
      ) ===
        comparableText(
          sourceText
        ),
  };
}




/*
 * Pure structural manuscript-displacement projection.
 *
 * This handles the authoring transition where an already
 * text-bearing Single Page becomes a physical page that
 * intentionally accepts no flowing manuscript, such as:
 *
 * - No text
 * - full-page illustration
 *
 * The saved manuscript belonging to the changed page must
 * not disappear merely because the new Book Structure says
 * that page has zero text capacity.
 *
 * The displaced structured manuscript is therefore placed
 * at the beginning of the next eligible downstream text
 * stream and the established projectRegion authority performs
 * the actual structured redistribution.
 *
 * This function does not:
 * - mutate Book Structure
 * - mutate manuscript entries
 * - create pages
 * - persist anything
 * - cross a structural barrier
 */
export function projectChildrensBookStructuralManuscriptDisplacement({
  previousUnits = [],
  nextUnits = [],
  entries = [],
  changedUnitId = "",
  wordLimit =
    MANUSCRIPT_PAGE_MAXIMUM_WORDS,
  slotWordLimitsByUnitId =
    null,
} = {}) {
  const normalizedChangedUnitId =
    clean(
      changedUnitId
    );

  if (
    !normalizedChangedUnitId
  ) {
    return failure(
      "structural_transition_unit_required",
      "A changed Book Structure unit is required for structural manuscript displacement."
    );
  }

  const beforeUnits =
    safeArray(
      previousUnits
    );

  const afterUnits =
    safeArray(
      nextUnits
    );

  const manuscriptEntries =
    safeArray(
      entries
    );

  const previousUnit =
    beforeUnits.find(
      (unit) =>
        clean(
          unit?.id
        ) ===
        normalizedChangedUnitId
    ) || null;

  const nextUnitIndex =
    afterUnits.findIndex(
      (unit) =>
        clean(
          unit?.id
        ) ===
        normalizedChangedUnitId
    );

  const nextUnit =
    nextUnitIndex >= 0
      ? afterUnits[
          nextUnitIndex
        ]
      : null;

  if (
    !previousUnit ||
    !nextUnit
  ) {
    return failure(
      "structural_transition_unit_missing",
      "The changed Book Structure unit must exist before and after the structural transition."
    );
  }

  const previousClassification =
    classifyFlowUnit(
      previousUnit
    );

  const nextClassification =
    classifyFlowUnit(
      nextUnit
    );

  if (
    previousClassification
      .kind !==
      "slot" ||
    nextClassification
      .kind !==
      "skip"
  ) {
    return {
      ok: true,
      applicable: false,
      reason:
        "transition_does_not_displace_manuscript",
      changedUnitId:
        normalizedChangedUnitId,
      displacedWordCount: 0,
      destinationUnitIds: [],
      nextBarrier: null,
      additionalPagesRequired: 0,
      projection: null,
    };
  }

  const displacedEntry =
    manuscriptEntries.find(
      (entry) =>
        clean(
          entry?.unitId
        ) ===
        normalizedChangedUnitId
    ) || null;

  if (!displacedEntry) {
    return {
      ok: true,
      applicable: false,
      reason:
        "no_displaced_manuscript",
      changedUnitId:
        normalizedChangedUnitId,
      displacedWordCount: 0,
      destinationUnitIds: [],
      nextBarrier: null,
      additionalPagesRequired: 0,
      projection: null,
    };
  }

  if (
    !hasOwn(
      displacedEntry,
      "manuscriptContent"
    )
  ) {
    if (
      clean(
        displacedEntry
          ?.manuscriptText
      )
    ) {
      return failure(
        "structured_content_required",
        "Structural manuscript displacement currently requires structured manuscript content."
      );
    }

    return {
      ok: true,
      applicable: false,
      reason:
        "no_displaced_manuscript",
      changedUnitId:
        normalizedChangedUnitId,
      displacedWordCount: 0,
      destinationUnitIds: [],
      nextBarrier: null,
      additionalPagesRequired: 0,
      projection: null,
    };
  }

  const displacedCombined =
    combinedStructuredContent([
      displacedEntry,
    ]);

  if (
    !displacedCombined.ok
  ) {
    return displacedCombined;
  }

  const displacedText =
    plainTextFromManuscriptContent(
      displacedCombined.content
    );

  const displacedWordCount =
    clean(
      displacedText
    )
      ? displacedText
          .trim()
          .split(/\s+/)
          .filter(Boolean)
          .length
      : 0;

  if (
    displacedWordCount < 1
  ) {
    return {
      ok: true,
      applicable: false,
      reason:
        "no_displaced_manuscript",
      changedUnitId:
        normalizedChangedUnitId,
      displacedWordCount: 0,
      destinationUnitIds: [],
      nextBarrier: null,
      additionalPagesRequired: 0,
      projection: null,
    };
  }

  const downstreamSlots = [];
  let nextBarrier = null;

  for (
    let index =
      nextUnitIndex + 1;
    index <
      afterUnits.length;
    index += 1
  ) {
    const unit =
      afterUnits[
        index
      ];

    const classification =
      classifyFlowUnit(
        unit
      );

    if (
      classification.kind ===
        "barrier"
    ) {
      nextBarrier = {
        unitId:
          classification.unitId,
        reason:
          classification.reason,
      };

      break;
    }

    if (
      classification.kind ===
        "slot"
    ) {
      downstreamSlots.push(
        unit
      );
    }
  }

  if (
    downstreamSlots.length <
      1
  ) {
    return {
      ok: true,
      applicable: true,
      reason:
        "no_downstream_text_pages",
      changedUnitId:
        normalizedChangedUnitId,
      displacedWordCount,
      destinationUnitIds: [],
      nextBarrier,
      additionalPagesRequired:
        Math.max(
          1,
          Math.ceil(
            displacedWordCount /
              Math.max(
                1,
                Number(
                  wordLimit
                ) ||
                  MANUSCRIPT_PAGE_MAXIMUM_WORDS
              )
          )
        ),
      projection: null,
    };
  }

  const entriesByUnitId =
    new Map(
      manuscriptEntries.map(
        (entry) => [
          clean(
            entry?.unitId
          ),
          entry,
        ]
      )
    );

  const firstDestinationUnitId =
    clean(
      downstreamSlots[0]
        ?.id
    );

  const firstDestinationEntry =
    entriesByUnitId.get(
      firstDestinationUnitId
    ) || null;

  const transitionSourceEntries = [
    displacedEntry,
  ];

  if (
    firstDestinationEntry
  ) {
    transitionSourceEntries.push(
      firstDestinationEntry
    );
  }

  const transitionCombined =
    combinedStructuredContent(
      transitionSourceEntries
    );

  if (
    !transitionCombined.ok
  ) {
    return transitionCombined;
  }

  const transientEntriesByUnitId =
    new Map(
      entriesByUnitId
    );

  transientEntriesByUnitId.set(
    firstDestinationUnitId,
    {
      ...(
        firstDestinationEntry ||
        {}
      ),

      unitId:
        firstDestinationUnitId,

      manuscriptText:
        plainTextFromManuscriptContent(
          transitionCombined
            .content
        ),

      manuscriptContent:
        transitionCombined
          .content,
    }
  );

  const projection =
    projectRegion({
      slotUnits:
        downstreamSlots,
      entriesByUnitId:
        transientEntriesByUnitId,
      wordLimit,
      slotWordLimitsByUnitId,
    });

  if (
    !projection.ok
  ) {
    return projection;
  }

  return {
    ok: true,
    applicable: true,
    reason:
      projection
        .additionalPagesRequired >
        0
        ? "additional_pages_required"
        : "displacement_fits_existing_pages",

    changedUnitId:
      normalizedChangedUnitId,

    displacedWordCount,

    destinationUnitIds:
      downstreamSlots.map(
        (unit) =>
          clean(
            unit?.id
          )
      ),

    nextBarrier,

    additionalPagesRequired:
      projection
        .additionalPagesRequired,

    projection,
  };
}


/*
 * Pure manuscript-flow projection.
 *
 * It does not:
 * - create or remove Book Map units
 * - alter illustration relationships
 * - persist anything
 * - mutate the supplied project plan
 * - cross unsupported structural barriers
 */
export function projectChildrensBookManuscriptFlow({
  units = [],
  entries = [],
  wordLimit =
    MANUSCRIPT_PAGE_MAXIMUM_WORDS,
  slotWordLimitsByUnitId =
    null,
} = {}) {
  const orderedUnits =
    safeArray(
      units
    );

  const manuscriptEntries =
    safeArray(
      entries
    );

  const {
    regions,
    skippedUnits,
    barrierUnits,
  } =
    buildFlowRegions(
      orderedUnits
    );

  const availablePageCount =
    regions.reduce(
      (
        total,
        region
      ) =>
        total +
        region.length,
      0
    );

  if (
    availablePageCount ===
      0
  ) {
    return failure(
      "no_eligible_pages",
      "No existing Single Pages are available for manuscript flow."
    );
  }

  const entriesByUnitId =
    new Map(
      manuscriptEntries.map(
        (entry) => [
          clean(
            entry?.unitId
          ),
          entry,
        ]
      )
    );

  const projectedRegions = [];

  for (
    let index = 0;
    index < regions.length;
    index += 1
  ) {
    const slotUnits =
      regions[index];

    const projection =
      projectRegion({
        slotUnits,
        entriesByUnitId,
        wordLimit,
        slotWordLimitsByUnitId,
      });

    if (!projection.ok) {
      return projection;
    }

    projectedRegions.push({
      index,

      firstUnitId:
        clean(
          slotUnits[0]?.id
        ),

      lastUnitId:
        clean(
          slotUnits[
            slotUnits.length - 1
          ]?.id
        ),

      slotUnitIds:
        slotUnits.map(
          (unit) =>
            clean(
              unit?.id
            )
        ),

      ...projection,
    });
  }

  const projectedPages =
    projectedRegions.flatMap(
      (region) =>
        region.projectedPages
    );

  const vacatedUnitIds =
    projectedRegions.flatMap(
      (region) =>
        region.vacatedUnitIds
    );

  const requiredPageCount =
    projectedRegions.reduce(
      (
        total,
        region
      ) =>
        total +
        region.requiredPageCount,
      0
    );

  const additionalPagesRequired =
    projectedRegions.reduce(
      (
        total,
        region
      ) =>
        total +
        region
          .additionalPagesRequired,
      0
    );

  const completeProjection =
    projectedRegions.every(
      (region) =>
        region
          .completeProjection
    );

  return {
    ok: true,

    projectedPages,

    vacatedUnitIds,

    flowRegions:
      projectedRegions,

    skippedUnits,

    barrierUnits,

    requiredPageCount,

    availablePageCount,

    additionalPagesRequired,

    completeProjection,

    manuscriptTextPreserved:
      projectedRegions.every(
        (region) =>
          region
            .manuscriptTextPreserved
      ),

    sourceContentMode:
      "structured",
  };
}


/*
 * Live consultation is intentionally bounded.
 *
 * The writer may work in a very large book. A small edit should not
 * require the browser to project hundreds of manuscript pages merely
 * to provide an advisory signal.
 *
 * This is not an application boundary. A later automatic-flow engine
 * may continue beyond this window incrementally.
 */
export const MANUSCRIPT_LIVE_FLOW_CONSULTATION_MAX_SLOTS =
  8;


export function consultChildrensBookManuscriptFlowFromUnit({
  units = [],
  entries = [],
  activeUnitId = "",
  wordLimit =
    MANUSCRIPT_PAGE_MAXIMUM_WORDS,
  maximumFlowSlots =
    MANUSCRIPT_LIVE_FLOW_CONSULTATION_MAX_SLOTS,
} = {}) {
  const normalizedActiveUnitId =
    clean(
      activeUnitId
    );

  if (
    !normalizedActiveUnitId
  ) {
    return failure(
      "active_unit_required",
      "Choose an active manuscript page before consulting live text flow."
    );
  }

  const orderedUnits =
    safeArray(
      units
    );

  const activeIndex =
    orderedUnits.findIndex(
      (unit) =>
        clean(
          unit?.id
        ) ===
        normalizedActiveUnitId
    );

  if (
    activeIndex < 0
  ) {
    return failure(
      "active_unit_not_found",
      "The active manuscript page could not be found in Book Structure."
    );
  }

  const requestedMaximum =
    Number(
      maximumFlowSlots
    );

  if (
    !Number.isInteger(
      requestedMaximum
    ) ||
    requestedMaximum < 1
  ) {
    return failure(
      "invalid_consultation_window",
      "The live-flow consultation window must contain at least one text page."
    );
  }

  const activeClassification =
    classifyFlowUnit(
      orderedUnits[
        activeIndex
      ]
    );

  if (
    activeClassification.kind !==
      "slot"
  ) {
    return {
      ok: true,

      consultable: false,

      activeUnitId:
        normalizedActiveUnitId,

      reason:
        activeClassification.reason,

      classification:
        activeClassification.kind,

      maximumFlowSlots:
        requestedMaximum,

      windowUnitIds: [],

      flowSlotUnitIds: [],

      skippedUnits: [],

      nextBarrier: null,

      windowTruncated: false,

      projection: null,
    };
  }

  const windowUnits = [];
  const flowSlotUnitIds = [];
  const skippedUnits = [];

  let nextBarrier = null;
  let windowTruncated = false;

  for (
    let index = activeIndex;
    index < orderedUnits.length;
    index += 1
  ) {
    const unit =
      orderedUnits[
        index
      ];

    const classification =
      classifyFlowUnit(
        unit
      );

    if (
      classification.kind ===
        "barrier"
    ) {
      nextBarrier = {
        unitId:
          classification.unitId,

        reason:
          classification.reason,
      };

      break;
    }

    if (
      classification.kind ===
        "slot" &&
      flowSlotUnitIds.length >=
        requestedMaximum
    ) {
      windowTruncated = true;
      break;
    }

    windowUnits.push(
      unit
    );

    if (
      classification.kind ===
        "slot"
    ) {
      flowSlotUnitIds.push(
        classification.unitId
      );
    } else {
      skippedUnits.push({
        unitId:
          classification.unitId,

        reason:
          classification.reason,
      });
    }
  }

  const windowUnitIds =
    new Set(
      windowUnits.map(
        (unit) =>
          clean(
            unit?.id
          )
      )
    );

  const windowEntries =
    safeArray(
      entries
    ).filter(
      (entry) =>
        windowUnitIds.has(
          clean(
            entry?.unitId
          )
        )
    );

  const projection =
    projectChildrensBookManuscriptFlow({
      units:
        windowUnits,

      entries:
        windowEntries,

      wordLimit,
    });

  if (
    !projection.ok
  ) {
    return projection;
  }

  return {
    ok: true,

    consultable: true,

    activeUnitId:
      normalizedActiveUnitId,

    maximumFlowSlots:
      requestedMaximum,

    windowUnitIds:
      windowUnits.map(
        (unit) =>
          clean(
            unit?.id
          )
      ),

    flowSlotUnitIds,

    skippedUnits,

    nextBarrier,

    windowTruncated,

    projection,
  };
}


/*
 * Application consultation follows the complete existing safe flow
 * region from the active page to the next structural barrier.
 *
 * The writer-facing live advisory remains independently bounded.
 */
export function consultChildrensBookManuscriptFlowForApplication({
  units = [],
  entries = [],
  activeUnitId = "",
  wordLimit =
    MANUSCRIPT_PAGE_MAXIMUM_WORDS,
} = {}) {
  const orderedUnits =
    safeArray(
      units
    );

  const normalizedActiveUnitId =
    clean(
      activeUnitId
    );

  const activeIndex =
    orderedUnits.findIndex(
      (unit) =>
        clean(
          unit?.id
        ) ===
        normalizedActiveUnitId
    );

  let applicationFlowSlots =
    1;

  if (
    normalizedActiveUnitId &&
    activeIndex >= 0
  ) {
    applicationFlowSlots =
      0;

    for (
      let index =
        activeIndex;
      index <
        orderedUnits.length;
      index += 1
    ) {
      const classification =
        classifyFlowUnit(
          orderedUnits[
            index
          ]
        );

      if (
        classification.kind ===
          "barrier"
      ) {
        break;
      }

      if (
        classification.kind ===
          "slot"
      ) {
        applicationFlowSlots +=
          1;
      }
    }

    if (
      applicationFlowSlots <
        1
    ) {
      applicationFlowSlots =
        1;
    }
  }

  return consultChildrensBookManuscriptFlowFromUnit({
    units:
      orderedUnits,
    entries,
    activeUnitId:
      normalizedActiveUnitId,
    wordLimit,
    maximumFlowSlots:
      applicationFlowSlots,
  });
}


/*
 * Pure existing-page manuscript-flow application preparation.
 *
 * This function does not:
 * - persist anything
 * - call a React state callback
 * - create or remove Book Map units
 * - alter illustration relationships
 * - mutate supplied entries
 *
 * It prepares a replacement manuscript-entry collection only when
 * the complete existing safe flow region can preserve the manuscript
 * stream inside already-existing eligible pages.
 */
export function prepareChildrensBookManuscriptFlowApplication({
  units = [],
  entries = [],
  activeUnitId = "",
  wordLimit =
    MANUSCRIPT_PAGE_MAXIMUM_WORDS,
} = {}) {
  const manuscriptEntries =
    safeArray(
      entries
    );

  const consultation =
    consultChildrensBookManuscriptFlowForApplication({
      units,
      entries:
        manuscriptEntries,
      activeUnitId,
      wordLimit,
    });

  if (
    !consultation?.ok
  ) {
    return consultation;
  }

  if (
    !consultation
      .consultable
  ) {
    return {
      ok: true,
      applicable: false,
      reason:
        consultation.reason ||
        "not_consultable",
      consultation,
      nextEntries:
        manuscriptEntries,
    };
  }

  const projection =
    consultation
      .projection;

  if (
    !projection?.ok
  ) {
    return failure(
      "flow_projection_unavailable",
      "Automatic manuscript flow could not prepare a safe projection."
    );
  }

  const completeProjection =
    projection
      .completeProjection ===
        true;

  const textPreserved =
    projection
      .manuscriptTextPreserved ===
        true;

  const additionalPagesRequired =
    Number(
      projection
        .additionalPagesRequired ||
      0
    );

  if (
    !completeProjection ||
    !textPreserved ||
    additionalPagesRequired > 0
  ) {
    return {
      ...failure(
        "existing_page_capacity_insufficient",
        "The existing manuscript pages cannot hold the complete projected text safely."
      ),
      applicable: false,
      additionalPagesRequired,
      consultation,
    };
  }

  const projectedPages =
    safeArray(
      projection
        .projectedPages
    );

  const projectedByUnitId =
    new Map(
      projectedPages.map(
        (page) => [
          clean(
            page?.unitId
          ),
          page,
        ]
      )
    );

  const vacatedUnitIds =
    safeArray(
      projection
        .vacatedUnitIds
    )
      .map(
        (unitId) =>
          clean(
            unitId
          )
      )
      .filter(Boolean);

  const vacated =
    new Set(
      vacatedUnitIds
    );

  const existingUnitIds =
    new Set();

  const nextEntries =
    manuscriptEntries.map(
      (entry) => {
        const unitId =
          clean(
            entry?.unitId
          );

        if (unitId) {
          existingUnitIds.add(
            unitId
          );
        }

        const projected =
          projectedByUnitId.get(
            unitId
          );

        if (projected) {
          return {
            ...entry,
            manuscriptText:
              projected
                .manuscriptText,
            manuscriptContent:
              projected
                .manuscriptContent,
          };
        }

        if (
          vacated.has(
            unitId
          )
        ) {
          return {
            ...entry,
            manuscriptText: "",
            manuscriptContent: {
              version:
                MANUSCRIPT_CONTENT_VERSION,
              blocks: [],
            },
          };
        }

        return entry;
      }
    );

  projectedPages.forEach(
    (projected) => {
      const unitId =
        clean(
          projected
            ?.unitId
        );

      if (
        !unitId ||
        existingUnitIds.has(
          unitId
        )
      ) {
        return;
      }

      nextEntries.push({
        unitId,
        manuscriptText:
          projected
            .manuscriptText,
        manuscriptContent:
          projected
            .manuscriptContent,
        authorNote: "",
        status:
          "not_started",
      });

      existingUnitIds.add(
        unitId
      );
    }
  );

  return {
    ok: true,
    applicable: true,
    consultation,
    nextEntries,
    projectedUnitIds:
      projectedPages
        .map(
          (page) =>
            clean(
              page?.unitId
            )
        )
        .filter(Boolean),
    vacatedUnitIds,
    additionalPagesRequired: 0,
    manuscriptTextPreserved: true,
  };
}



/*
 * Writer-directed sentence boundary movement.
 *
 * This is deliberately separate from automatic balancing.
 * The creator chooses the direction of movement; this helper then
 * changes only the boundary between the active manuscript page and
 * the next eligible existing manuscript-flow page.
 *
 * It does not:
 * - persist anything;
 * - create or remove Book Structure units;
 * - cross a structural barrier;
 * - change manuscript wording or order;
 * - flatten structured formatting.
 */
const MANUSCRIPT_NON_TERMINAL_SENTENCE_ABBREVIATIONS =
  new Set([
    "mr.",
    "mrs.",
    "ms.",
    "dr.",
    "prof.",
    "rev.",
    "gen.",
    "col.",
    "capt.",
    "lt.",
    "sgt.",
    "sen.",
    "rep.",
    "gov.",
    "pres.",
    "m.",
    "mme.",
    "mmes.",
    "mlle.",
    "pr.",
    "e.g.",
    "i.e.",
  ]);


function manuscriptWordEndsSentence(
  word,
  index,
  words
) {
  const requested =
    String(
      word ?? ""
    );

  if (
    !/[.!?…](?:["'’”»)\]]+)?$/.test(
      requested
    )
  ) {
    return false;
  }

  const withoutClosingMarks =
    requested.replace(
      /["'’”»)\]]+$/g,
      ""
    );

  const normalized =
    withoutClosingMarks
      .toLowerCase();

  const hasFollowingWord =
    Number(index) <
      words.length - 1;

  if (
    hasFollowingWord &&
    MANUSCRIPT_NON_TERMINAL_SENTENCE_ABBREVIATIONS
      .has(
        normalized
      )
  ) {
    return false;
  }

  /*
   * A single-letter initial such as "J." in "J. R. Tolkien"
   * is not treated as a sentence ending while more words follow.
   */
  if (
    hasFollowingWord &&
    /^[A-Za-zÀ-ÖØ-öø-ÿ]\.$/.test(
      withoutClosingMarks
    )
  ) {
    return false;
  }

  return true;
}


function manuscriptSentenceBoundaryWords(
  value
) {
  const words =
    Array.from(
      String(
        value ?? ""
      ).matchAll(
        /\S+/g
      )
    ).map(
      (match) =>
        match[0]
    );

  const boundaries = [];

  words.forEach(
    (
      word,
      index
    ) => {
      if (
        manuscriptWordEndsSentence(
          word,
          index,
          words
        )
      ) {
        boundaries.push(
          index + 1
        );
      }
    }
  );

  return {
    words,
    boundaries,
  };
}



export function manuscriptTextBreakIsSentenceBoundary(
  before,
  after
) {
  const beforeLayout =
    manuscriptSentenceBoundaryWords(
      before
    );

  const afterLayout =
    manuscriptSentenceBoundaryWords(
      after
    );

  const boundaryWordCount =
    beforeLayout.words.length;

  if (
    boundaryWordCount < 1 ||
    afterLayout.words.length < 1
  ) {
    return false;
  }

  const combinedLayout =
    manuscriptSentenceBoundaryWords(
      `${String(
        before ?? ""
      )} ${String(
        after ?? ""
      )}`
    );

  return (
    combinedLayout
      .boundaries
      .includes(
        boundaryWordCount
      )
  );
}


function structuredManuscriptWordCount(
  entry
) {
  const structured =
    structuredEntryContent(
      entry
    );

  if (!structured.ok) {
    return structured;
  }

  return {
    ok: true,

    wordCount:
      manuscriptSentenceBoundaryWords(
        plainTextFromManuscriptContent(
          structured.content
        )
      ).words.length,

    content:
      structured.content,
  };
}


function nextExistingManuscriptFlowSlot(
  units,
  activeIndex
) {
  for (
    let index =
      activeIndex + 1;
    index <
      safeArray(
        units
      ).length;
    index += 1
  ) {
    const unit =
      units[index];

    const classification =
      classifyFlowUnit(
        unit
      );

    if (
      classification.kind ===
        "barrier"
    ) {
      return {
        unit: null,
        index: -1,
        barrier:
          classification,
      };
    }

    if (
      classification.kind ===
        "slot"
    ) {
      return {
        unit,
        index,
        barrier: null,
      };
    }
  }

  return {
    unit: null,
    index: -1,
    barrier: null,
  };
}


function replaceSentenceBoundaryEntries({
  entries,
  activeUnitId,
  targetUnitId,
  activeSegment,
  targetSegment,
}) {
  const manuscriptEntries =
    safeArray(
      entries
    );

  const nextEntries = [];

  let targetPresent = false;

  manuscriptEntries.forEach(
    (entry) => {
      const unitId =
        clean(
          entry?.unitId
        );

      if (
        unitId ===
          activeUnitId
      ) {
        nextEntries.push({
          ...entry,

          manuscriptText:
            activeSegment
              .manuscriptText,

          manuscriptContent:
            activeSegment
              .manuscriptContent,
        });

        return;
      }

      if (
        unitId ===
          targetUnitId
      ) {
        targetPresent = true;

        nextEntries.push({
          ...entry,

          manuscriptText:
            targetSegment
              ?.manuscriptText ||
            "",

          manuscriptContent:
            targetSegment
              ?.manuscriptContent || {
              version:
                MANUSCRIPT_CONTENT_VERSION,
              blocks: [],
            },
        });

        return;
      }

      nextEntries.push(
        entry
      );
    }
  );

  if (
    !targetPresent &&
    targetSegment
  ) {
    nextEntries.push({
      unitId:
        targetUnitId,

      manuscriptText:
        targetSegment
          .manuscriptText,

      manuscriptContent:
        targetSegment
          .manuscriptContent,

      authorNote: "",

      status:
        "not_started",
    });
  }

  return nextEntries;
}


export function prepareChildrensBookManuscriptSentenceBoundaryMove({
  units = [],
  entries = [],
  activeUnitId = "",
  action = "",
  wordLimit =
    MANUSCRIPT_PAGE_MAXIMUM_WORDS,
} = {}) {
  const normalizedActiveUnitId =
    clean(
      activeUnitId
    );

  const requestedAction =
    clean(
      action
    );

  const allowedActions =
    new Set([
      "move_last_sentence_forward",
      "bring_next_sentence_backward",
    ]);

  if (
    !normalizedActiveUnitId
  ) {
    return failure(
      "active_unit_required",
      "Choose an active manuscript page before moving a sentence boundary."
    );
  }

  if (
    !allowedActions.has(
      requestedAction
    )
  ) {
    return failure(
      "sentence_boundary_action_invalid",
      "Choose a supported sentence-boundary action."
    );
  }

  const orderedUnits =
    safeArray(
      units
    );

  const activeIndex =
    orderedUnits.findIndex(
      (unit) =>
        clean(
          unit?.id
        ) ===
        normalizedActiveUnitId
    );

  if (
    activeIndex < 0
  ) {
    return failure(
      "active_unit_not_found",
      "The active manuscript page could not be found in Book Structure."
    );
  }

  const activeUnit =
    orderedUnits[
      activeIndex
    ];

  const activeClassification =
    classifyFlowUnit(
      activeUnit
    );

  if (
    activeClassification.kind !==
      "slot"
  ) {
    return {
      ok: true,
      applied: false,
      reason:
        activeClassification.reason ||
        "active_page_not_flowable",
      nextEntries:
        safeArray(
          entries
        ),
    };
  }

  const nextSlot =
    nextExistingManuscriptFlowSlot(
      orderedUnits,
      activeIndex
    );

  if (
    !nextSlot.unit
  ) {
    return {
      ok: true,
      applied: false,
      reason:
        nextSlot.barrier
          ? "next_structural_barrier"
          : "next_manuscript_page_unavailable",
      barrier:
        nextSlot.barrier,
      nextEntries:
        safeArray(
          entries
        ),
    };
  }

  const targetUnit =
    nextSlot.unit;

  const targetUnitId =
    clean(
      targetUnit?.id
    );

  const manuscriptEntries =
    safeArray(
      entries
    );

  const activeEntry =
    manuscriptEntries.find(
      (entry) =>
        clean(
          entry?.unitId
        ) ===
        normalizedActiveUnitId
    );

  if (!activeEntry) {
    return failure(
      "active_manuscript_entry_required",
      "The active page has no structured manuscript entry to move."
    );
  }

  const activeStructured =
    structuredManuscriptWordCount(
      activeEntry
    );

  if (!activeStructured.ok) {
    return activeStructured;
  }

  const targetEntry =
    manuscriptEntries.find(
      (entry) =>
        clean(
          entry?.unitId
        ) ===
        targetUnitId
    );

  if (
    targetEntry &&
    !hasOwn(
      targetEntry,
      "manuscriptContent"
    ) &&
    clean(
      targetEntry
        ?.manuscriptText
    )
  ) {
    return failure(
      "structured_content_required",
      "Sentence-boundary movement requires structured manuscript content on the next manuscript page."
    );
  }

  const combinedEntries = [
    activeEntry,
  ];

  if (
    targetEntry &&
    hasOwn(
      targetEntry,
      "manuscriptContent"
    )
  ) {
    combinedEntries.push(
      targetEntry
    );
  }

  const combined =
    combinedStructuredContent(
      combinedEntries
    );

  if (!combined.ok) {
    return combined;
  }

  const combinedText =
    plainTextFromManuscriptContent(
      combined.content
    );

  const sentenceLayout =
    manuscriptSentenceBoundaryWords(
      combinedText
    );

  const activeWordCount =
    activeStructured
      .wordCount;

  const totalWordCount =
    sentenceLayout
      .words
      .length;

  if (
    totalWordCount < 1 ||
    activeWordCount < 1
  ) {
    return {
      ok: true,
      applied: false,
      reason:
        "sentence_boundary_text_unavailable",
      nextEntries:
        manuscriptEntries,
    };
  }

  let requestedBoundary =
    null;

  if (
    requestedAction ===
      "move_last_sentence_forward"
  ) {
    const candidates =
      sentenceLayout
        .boundaries
        .filter(
          (boundary) =>
            boundary > 0 &&
            boundary <
              activeWordCount
        );

    requestedBoundary =
      candidates.length
        ? candidates[
            candidates.length -
              1
          ]
        : null;
  } else {
    requestedBoundary =
      sentenceLayout
        .boundaries
        .find(
          (boundary) =>
            boundary >
              activeWordCount
        ) ?? null;
  }

  if (
    !Number.isInteger(
      requestedBoundary
    )
  ) {
    return {
      ok: true,
      applied: false,
      reason:
        requestedAction ===
          "move_last_sentence_forward"
          ? "previous_sentence_boundary_unavailable"
          : "next_sentence_boundary_unavailable",
      nextEntries:
        manuscriptEntries,
    };
  }

  const normalizedBaseWordLimit =
    Number(
      wordLimit
    );

  if (
    !Number.isInteger(
      normalizedBaseWordLimit
    ) ||
    normalizedBaseWordLimit < 1
  ) {
    return failure(
      "invalid_word_limit",
      "A positive manuscript page word limit is required."
    );
  }

  const activeWordLimit =
    manuscriptFlowSlotWordLimit(
      activeUnit,
      normalizedBaseWordLimit
    );

  const targetWordLimit =
    manuscriptFlowSlotWordLimit(
      targetUnit,
      normalizedBaseWordLimit
    );

  if (
    !Number.isInteger(
      activeWordLimit
    ) ||
    activeWordLimit < 1 ||
    !Number.isInteger(
      targetWordLimit
    ) ||
    targetWordLimit < 1
  ) {
    return failure(
      "invalid_flow_slot_word_limit",
      "Sentence-boundary movement could not determine a safe word capacity for the manuscript pages."
    );
  }

  const nextActiveWordCount =
    requestedBoundary;

  const nextTargetWordCount =
    totalWordCount -
    requestedBoundary;

  if (
    nextActiveWordCount >
      activeWordLimit
  ) {
    return {
      ok: true,
      applied: false,
      reason:
        "active_page_capacity_insufficient",
      activeWordLimit,
      requestedWordCount:
        nextActiveWordCount,
      nextEntries:
        manuscriptEntries,
    };
  }

  if (
    nextTargetWordCount >
      targetWordLimit
  ) {
    return {
      ok: true,
      applied: false,
      reason:
        "next_page_capacity_insufficient",
      targetWordLimit,
      requestedWordCount:
        nextTargetWordCount,
      nextEntries:
        manuscriptEntries,
    };
  }

  const proposedSegments = [
    sentenceLayout
      .words
      .slice(
        0,
        requestedBoundary
      )
      .join(
        " "
      ),
  ];

  if (
    requestedBoundary <
      totalWordCount
  ) {
    proposedSegments.push(
      sentenceLayout
        .words
        .slice(
          requestedBoundary
        )
        .join(
          " "
        )
    );
  }

  const division =
    buildChildrensBookStructuredManuscriptDivision({
      manuscriptContent:
        combined.content,

      segments:
        proposedSegments,

      wordLimit:
        Math.max(
          activeWordLimit,
          targetWordLimit
        ),
    });

  if (!division.ok) {
    return {
      ...division,
      applied: false,
      nextEntries:
        manuscriptEntries,
    };
  }

  const activeSegment =
    division
      .segments[0];

  const targetSegment =
    division
      .segments[1] ||
      null;

  if (!activeSegment) {
    return failure(
      "sentence_boundary_materialization_failed",
      "The requested sentence boundary could not be materialized safely."
    );
  }

  const nextEntries =
    replaceSentenceBoundaryEntries({
      entries:
        manuscriptEntries,

      activeUnitId:
        normalizedActiveUnitId,

      targetUnitId,

      activeSegment,

      targetSegment,
    });

  return {
    ok: true,
    applied: true,

    action:
      requestedAction,

    activeUnitId:
      normalizedActiveUnitId,

    targetUnitId,

    previousActiveWordCount:
      activeWordCount,

    activeWordCount:
      nextActiveWordCount,

    targetWordCount:
      nextTargetWordCount,

    movedWordCount:
      Math.abs(
        requestedBoundary -
          activeWordCount
      ),

    activeWordLimit,
    targetWordLimit,

    nextEntries,

    manuscriptTextPreserved:
      true,

    manuscriptStructurePreserved:
      division
        .summary
        ?.manuscriptStructurePreserved ===
        true,

    formattingPreserved:
      division
        .summary
        ?.formattingPreserved ===
        true,
  };
}
